import Price from "./price.js";
import xss from "xss";

class CurrencyItem {
  constructor(props) {
    const {
      kennziffer, id, name, symbol, iso, rate, denomination, innenProvision, minDenomination,
      positionDues, positionSum, locked, popularity, produktInfo,
    } = props || {};
    this.kennziffer = kennziffer;
    this.id = String(id).toLowerCase();
    this.name = xss(String(name));
    this.symbol = xss(String(symbol));
    this.iso = xss(String(iso));
    this.rate = Number(rate) || 1;
    this.denomination = Array.isArray(denomination) ? denomination.map(d => Number(d)) : [];
    this.minDenomination = Number(minDenomination) || 1;
    this.selectedIso = xss(String(iso));
    this.image = String(iso).replace(/[^a-zA-Z0-9]/, "") + ".png";
    this.selectedDenomination = null;
    this.hasDenominator = this.denomination.length > 0;
    this.hasSelectedDenomination = false;
    this.amount = 0;
    this.amountModel = 0; // used as local model for the basket-adder !
    this.dirty = false;
    this.euroShown = true;
    this.price = Price.create(0);
    this.innenProvision = Number(innenProvision) || 0;
    this.positionDues = Price.create(positionDues);
    this.positionSum = Price.create(positionSum);
    this.popularity = !popularity
      ? 99999
      : popularity;
    this.selectionArray = [
      { value: xss(String(iso)), text: xss(String(iso).toUpperCase()) },
      { value: "eur", text: "EUR" }
    ];
    this.locked = !!locked;
    this.produktInfo = xss(produktInfo);
  }
  static create({
    kennziffer, id, name, symbol, iso, rate, denomination, innenProvision, minDenomination, positionDues, positionSum, popularity, produktInfo
  }) {
    return new CurrencyItem({
      kennziffer,
      id,
      name,
      symbol,
      iso,
      rate,
      denomination,
      innenProvision,
      minDenomination,
      positionDues,
      positionSum,
      popularity,
      produktInfo,
    });
  }
  static parseInt(txt) {
    txt = parseInt(String(txt), 10);
    if (isNaN(txt))
      return 0;
    return txt;
  }
  static ensureIsFraction(amount, fraction) {
    return Math.ceil(amount / fraction) * fraction;
  }
  setPositionDues(num) {
    this.positionDues = Price.create(num);
  }
  getPrice() {
    return this.price;
  }
  getUnit() {
    return Price.formatUnit(this.iso);
  }
  getAmount() {
    return this.amount;
  }
  // helper for denominator
  getEuroAmount() {
    return Math.round((this.amount / this.rate) * 100) / 100;
  }
  toString() {
    if (this.euroShown) {
      return this.toEuro();
    }
    return this.toIso();
  }
  toEuro() {
    return this.price.toEuro();
  }
  toIso() {
    return <span className="nowrap">{Price.formatify(this.amount) + " " + this.getUnit()}</span>;
  }
  getDisplayRate() {
    let parts = String(Math.round(this.rate * 100000) / 100000).split(".");
    if (parts.length < 2)
      return parts[0] + ",00";
    if (parts[1].length < 2)
      return parts[0] + "," + parts[1] + "0";
    return parts[0] + "," + parts[1];
  }
  reset() {
    this.amount = 0;
    this.amountModel = 0;
    this.price.set(0);
    this.euroShown = true;
    return this;
  }
  isZero() {
    return this.amount === 0;
  }
  showEuro() {
    this.euroShown = true;
    return this;
  }
  showIso() {
    this.euroShown = false;
    return this;
  }
  setAmount(amount) {
    this.amount = Math.floor(amount);
    this.price.setPrice(this.amount / this.rate);
    if (this.selectedIso === "eur")
      this.amountModel = Math.round(this.amount / this.rate);
    else
      this.amountModel = this.amount;
    return this;
  }
  addAmount(toAdd) {
    return this.setAmount(this.amount + Math.round(CurrencyItem.parseInt(toAdd)));
  }
  setAmountModel(amountModel) {
    let amount = CurrencyItem.parseInt(amountModel);
    if (this.selectedIso === "eur") {
      this.amountModel = amount;
      this.amount = amount * this.rate;
      this.price.setPrice(amount / this.rate);
      this.showIso();
    } else {
      this.setAmount(amount).showEuro();
    }
    return this;
  }
  // helper, updates changed amountModel invalidates selected denomination
  doUpdateAmountModel() {
    let amount = CurrencyItem.parseInt(this.amountModel);
    if (this.selectedIso === "eur") {
      this.setAmount(CurrencyItem.ensureIsFraction(amount * this.rate, this.minDenomination)).showIso();
    } else {
      this.setAmount(CurrencyItem.ensureIsFraction(amount, this.minDenomination)).showEuro();
    }
    this.setSelectedDenomination(null);
    return this;
  }
  getSelectedIso() {
    return String(this.selectedIso).toLowerCase() === "eur" ? "eur" : this.iso;
  }
  setSelectedIso(iso) {
    this.selectedIso = String(iso).toLowerCase() === "eur" ? "eur" : this.iso;
    if (this.selectedIso === "eur") {
      this.amountModel = this.amount / this.rate;
    } else {
      this.amountModel = this.amount;
    }
    this.selectionArray = [
      { value: this.iso, text: this.iso.toUpperCase() },
      { value: "eur", text: "EUR" }
    ];
    return this;
  }
  setSelectedDenomination(denomination) {
    if (denomination != null && denomination.items.length > 0) {
      this.selectedDenomination = {
        ...denomination,
        items: denomination.items.map(item => ({ ...item }))
      };
      this.hasSelectedDenomination = true;
    } else {
      this.selectedDenomination = null;
      this.hasSelectedDenomination = false;
    }
    return this;
  }
  cloneSelectedDenomination() {
    if (this.selectedDenomination === null || this.selectedDenomination.items.length === 0)
      return null;
    return {
      ...this.selectedDenomination,
      items: this.selectedDenomination.items.map(item => ({ ...item }))
    };
  }
  // used in basket for recreation after commit
  recreate({ rate, price, dirty }) {
    this.rate = rate;
    if (price) {
      this.price.setPrice(price);
    } else {
      this.setAmountModel(this.amountModel);
    }
    this.dirty = !!dirty;
  }
  toJSONForCart() {
    let data = this.toJSONForOrderRequest();
    data.gebuehren = this.positionDues.getPrice();
    return data;
  }
  toJSONForOrderRequest() {
    let stueckelung = [], betragStueckelung = [];
    if (this.selectedDenomination != null && Array.isArray(this.selectedDenomination.items)) {
      stueckelung = this.selectedDenomination.items.map(item => item.note);
      betragStueckelung = this.selectedDenomination.items.map(item => item.amount);
    }
    // stueckelung is sent as comma-seperated strings
    return {
      /* SEFRO-539 : kennziffer: this.kennziffer, */
      iso: this.iso.toUpperCase(),
      euro: this.price.getPrice(),
      fremdwaehrung: this.amount,
      stueckelung: stueckelung.join(","),
      betragStueckelung: betragStueckelung.join(","),
      rate: this.rate,
    };
  }
  toJSONForOrder() {
    let data = this.toJSONForCart();
    data.brutto = this.positionSum.getPrice();
    data.image = this.image;
    data.name = this.name;
    data.produktInfo = this.produktInfo;
    return data;
  }
  clone(newId) {
    let item = CurrencyItem.create({
      ...this,
      id: newId,
    });
    if (!this.euroShown) {
      item.selectedIso = "eur";
    }
    item.setAmount(this.amount);
    item.selectedIso = "eur";
    item.setSelectedDenomination(this.selectedDenomination);
    item.showEuro();
    return item;
  }
}

export default CurrencyItem;
